import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { GuardedDialog, isDirty, useOpenSnapshot } from "@/components/support/GuardedDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PRIORITIES, STATUSES, PRIORITY_COLOR, STATUS_COLOR } from "@/lib/supportApi";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { customer_id: "", subject: "", description: "", priority: "Normal" };

export default function Tickets() {
  const { currentOrgId } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [machineId, setMachineId] = useState("");
  const [f, setF] = useState(EMPTY);
  const snap = useOpenSnapshot(open, f);

  const load = () => { if (currentOrgId) api.get(`/support/tickets?org_id=${currentOrgId}${status !== "all" ? `&status=${encodeURIComponent(status)}` : ""}`).then((r) => setRows(r.data)).catch(() => {}); };
  useEffect(load, [currentOrgId, status]);
  useEffect(() => { if (currentOrgId) api.get(`/support/customers?org_id=${currentOrgId}&status=active`).then((r) => setCustomers(r.data)).catch(() => {}); }, [currentOrgId]);

  // Prefill from a machine's "Create support ticket" action.
  useEffect(() => {
    if (sp.get("new") === "1") {
      const cid = sp.get("customer_id") || "";
      setMachineId(sp.get("machine_id") || "");
      setF((p) => ({ ...p, customer_id: cid }));
      setOpen(true);
      sp.delete("new"); sp.delete("machine_id"); sp.delete("customer_id"); setSp(sp, { replace: true });
    }
  }, [sp, setSp]);

  const create = async () => {
    if (!f.customer_id || !f.subject.trim()) { toast.error("Customer and subject required"); return; }
    try {
      const r = await api.post("/support/tickets", { org_id: currentOrgId, ...f, machine_id: machineId || null, source: machineId ? "device" : "manual" });
      setOpen(false); setMachineId(""); setF(EMPTY);
      toast.success(`Ticket #${r.data.ticket_number} created`); navigate(`/helpdesk/tickets/${r.data.ticket_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create ticket"); }
  };

  return (
    <div className="space-y-4" data-testid="tickets-page">
      <div className="flex items-center justify-between gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="ticket-status-filter" className="w-48 bg-black border-zinc-800"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="new-ticket-button" onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-500"><Plus size={15} className="mr-1.5" /> New ticket</Button>
      </div>
      <div className="rounded-xl border border-white/10 bg-zinc-900/60 divide-y divide-white/5">
        {rows.map((t) => (
          <button key={t.ticket_id} data-testid={`ticket-row-${t.ticket_id}`} onClick={() => navigate(`/helpdesk/tickets/${t.ticket_id}`)} className="w-full text-left flex items-center justify-between px-4 py-3 hover:bg-white/5">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-zinc-500">#{t.ticket_number}</span>
                <span className="text-sm truncate">{t.subject}</span>
              </div>
              <div data-testid={`ticket-customer-${t.ticket_id}`} className="text-xs text-blue-300/80 mt-0.5">{t.customer_name || "No customer"}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PRIORITY_COLOR[t.priority]}`}>{t.priority}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STATUS_COLOR[t.status]}`}>{t.status}</span>
            </div>
          </button>
        ))}
        {rows.length === 0 && <div className="p-8 text-center text-sm text-zinc-500">No tickets</div>}
      </div>

      <GuardedDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setMachineId(""); setF(EMPTY); } }} dirty={isDirty(f, snap)} className="bg-zinc-900 border-white/10" testid="ticket-dialog">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">New ticket</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">Open a support ticket for a customer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div><Label className="text-xs text-zinc-400">Customer *</Label>
              <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v })}>
                <SelectTrigger data-testid="ticket-customer-select" className="mt-1 bg-black border-zinc-800"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">{customers.map((c) => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-zinc-400">Subject *</Label><Input data-testid="ticket-subject-input" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="mt-1 bg-black border-zinc-800" /></div>
            <div><Label className="text-xs text-zinc-400">Priority</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                <SelectTrigger className="mt-1 bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-zinc-400">Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} className="mt-1 bg-black border-zinc-800" /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="ghost" data-testid="ticket-cancel-button" className="text-zinc-400">Cancel</Button></DialogClose>
            <Button data-testid="ticket-create-submit" onClick={create} className="bg-blue-600 hover:bg-blue-500">Create</Button>
          </DialogFooter>
      </GuardedDialog>
    </div>
  );
}
